import React from 'react';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Form, Input, Row, Col, Button, Checkbox } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Authorize from 'components/Providers/Authorize';
import PageWrapper from 'components/Shared/Page/Wrapper';
import PageTitle from 'components/Shared/Page/Title';
import withPusher from 'components/Providers/Pusher/withPusher';
import PageBanners from 'components/Layout/PageBanners';
import { WALLET_NAME_MIN_LENGTH, WALLET_NAME_MAX_LENGTH } from 'constants/wallet';
import useTranslation from 'utils/hooks/useTranslation';
import useIsFormValid from 'utils/hooks/useIsFormValid';
import useValidateFormOnLocaleChange from 'utils/hooks/useValidateFormOnLocaleChange';
import useCreateWalletPage from './useCreateWalletPage';
import HowWalletWorksGrid from './HowWalletWorksGrid';
import LowBalanceAlert from './LowBalanceAlert';

import styles from './style.module.scss';

const walletNameAllowedCharactersRegex = /^[^<>"'&]+$/;

const fieldValidationConfig = [
  { name: 'walletName', required: true },
  { name: 'currency', required: false },
  { name: 'lowBalanceOtherAmount', required: false },
  { name: 'termsAndConditions', required: true },
];

const CreateWallet = () => {
  const history = useHistory();
  const { translateText } = useTranslation();
  const {
    currency,
    termsUrl,
    companyName,
    isCreateWalletLoading,
    onCreateWallet,
    onBack,
    onLowBalanceAlertAmountSelected,
  } = useCreateWalletPage();

  const [form] = Form.useForm();
  const { isFormValid, validateForm } = useIsFormValid(form, fieldValidationConfig);

  useValidateFormOnLocaleChange(form);

  const walletNameLengthMessage = translateText(
    'corporateAccounts.wallet.create.form.walletNameLength',
    { minLength: WALLET_NAME_MIN_LENGTH, maxLength: WALLET_NAME_MAX_LENGTH },
  );

  return (
    <Authorize>
      <Helmet title="Create Wallet" />
      <PageBanners />
      <div>
        <PageWrapper history={history} loading={false}>
          <div className={styles.pageContent}>
            <PageTitle
              title={translateText('corporateAccounts.wallet.create.title')}
              description={translateText('corporateAccounts.wallet.create.description')}
            />
            <div className="card-body">
              <Form
                form={form}
                layout="vertical"
                onFieldsChange={validateForm}
                onFinish={onCreateWallet}
              >
                <Row gutter={16}>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name="walletName"
                      label={
                        translateText('corporateAccounts.wallet.create.form.walletName') + ' *'
                      }
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: translateText(
                            'corporateAccounts.wallet.create.form.walletNameRequired',
                          ),
                        },
                        {
                          pattern: walletNameAllowedCharactersRegex,
                          message: translateText(
                            'corporateAccounts.wallet.create.form.walletNameInvalidCharacters',
                          ),
                        },
                        { max: WALLET_NAME_MAX_LENGTH, message: walletNameLengthMessage },
                        { min: WALLET_NAME_MIN_LENGTH, message: walletNameLengthMessage },
                      ]}
                    >
                      <Input
                        placeholder={translateText(
                          'corporateAccounts.wallet.create.form.walletNamePlaceholder',
                        )}
                        className={styles.formInput}
                        data-testid="walletNameInput"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="currency"
                      label={translateText('corporateAccounts.wallet.create.form.currency')}
                      initialValue={translateText(
                        `corporateAccounts.wallet.create.currency.${currency.code}`,
                      )}
                    >
                      <Input
                        disabled
                        className={styles.formInput}
                        data-testid="walletCurrencyInput"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <HowWalletWorksGrid />
                <LowBalanceAlert
                  currencySymbol={currency.symbol}
                  form={form}
                  onAmountSelected={onLowBalanceAlertAmountSelected}
                />
                <Form.Item
                  name="termsAndConditions"
                  initialValue={false}
                  valuePropName="checked"
                  className={styles.termsAndConditions}
                  data-testid="walletTermsAndConditions"
                  rules={[
                    {
                      validator: (rule, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              translateText(
                                'corporateAccounts.wallet.create.termsAndConditionsRequired',
                              ),
                            ),
                    },
                  ]}
                >
                  <Checkbox data-testid="walletTermsCheckbox">
                    {translateText('corporateAccounts.wallet.create.termsAndConditions', {
                      termsAndConditionsLink: (
                        <a
                          href={termsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="walletTermsLink"
                          className={styles.termsAndConditionsLink}
                        >
                          {translateText(
                            'corporateAccounts.wallet.create.termsAndConditionsLinkText',
                          )}
                        </a>
                      ),
                      companyName,
                    })}
                  </Checkbox>
                </Form.Item>
                <div className={styles.footer}>
                  <Button
                    type="text"
                    icon={<LeftOutlined />}
                    className={`${styles.footerButton} ${styles.backButton}`}
                    data-testid="createWalletBackButton"
                    onClick={onBack}
                  >
                    {translateText('corporateAccounts.actions.back')}
                  </Button>
                  <div className={styles.footerActions}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={!isFormValid}
                      loading={isCreateWalletLoading}
                      className={styles.footerButton}
                      data-testid="createWalletSubmitButton"
                    >
                      {translateText('corporateAccounts.wallet.create.submit')}
                      <RightOutlined className={styles.footerButtonIcon} />
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </PageWrapper>
      </div>
    </Authorize>
  );
};

export default withPusher(injectIntl(CreateWallet));
